/* @jsx h */
import { defineComponent, ref, watch, h, onMounted, onUnmounted } from "vue";
import { RouterView, useRoute } from "vue-router";
import { ShopLayout } from "../Shop/components/index";
import { useCustomer, useMenu } from "../../providers";
import { fetchGroupOrder, joinGroupOrder } from "@app/server/client";
import { Loading } from "@lemmonhq/uikit";
import { computed } from "vue";
import { ShopCheckoutStatusPage } from "../ShopCheckoutStatus";
import { ShopCheckoutResultPage } from "../ShopCheckoutResult";
import { JoinGroupModal } from "./components/JoinGroupModal";
import { GroupOrder } from "@lemmonhq/types";
import { nanoid } from "nanoid";

export const ShopGroupOrderPage = defineComponent({
  setup() {
    const route = useRoute();
    const { groupOrder, cart, refetchGroupOrder, shop, realtime } = useMenu();
    const loaded = ref(false);
    const { customer } = useCustomer();
    const showJoinModal = ref(false);
    const isJoining = ref(false);

    // Check if current user has a checkout
    const hasCheckout = computed(() => {
      if (!customer.value?.id || !groupOrder.value?.checkouts) {
        return false;
      }
      return false;
      //      return groupOrder.value.checkouts.some(
      //        (checkout) =>
      //          checkout.customer_id === customer.value?.id &&
      //          checkout.status === "new"
      //      );
    });

    // Check if current user has already joined
    const hasJoined = computed(() => {
      if (!customer.value?.id || !groupOrder.value?.members) {
        return false;
      }
      return groupOrder.value.members.some(
        (member) => member.customer_id === customer.value?.id,
      );
    });

    // Handle joining the group order
    const handleJoin = async () => {
      if (!customer.value?.id || !groupOrder.value?.id || isJoining.value) {
        return;
      }

      try {
        isJoining.value = true;
        await joinGroupOrder(groupOrder.value.id, customer.value.id);

        // Refetch the group order to get updated members list
        await refetchGroupOrder();

        showJoinModal.value = false;

        // Enable realtime after joining
        realtime.enable(groupOrder.value as GroupOrder);
      } catch (error) {
        console.error("Failed to join group order:", error);
      } finally {
        isJoining.value = false;
      }
    };

    // Initialize group order on mount or route change
    onMounted(async () => {
      console.log("route.params changed", route.params);
      const groupId = route.params.groupSlug as string;
      if (groupId && groupOrder.value?.id !== groupId) {
        const go = await fetchGroupOrder(groupId);
        groupOrder.value = go;

        // Setup cart when groupOrder is loaded (centralized cart setup)
        if (shop.value) {
          console.log("Setting up cart in ShopGroupOrder for:", {
            shopId: shop.value.id,
            groupOrderId: go.id,
          });
          await cart.setupShop(shop.value, go.id);
        }

        loaded.value = true;

        // Check if user has already joined
        const userHasJoined = go.members?.some(
          (member) => member.customer_id === customer.value?.id,
        );

        if (userHasJoined) {
          // User has joined, enable realtime
          console.log("User has already joined, enabling realtime");
          realtime.enable(go as GroupOrder);
        } else if (go.table_id) {
          // User hasn't joined and this is a table order, show join modal
          console.log("User hasn't joined, showing join modal");
          showJoinModal.value = true;
        }
      }
    });

    // Set up interval to refetch group order every 10 seconds
    let refetchInterval: NodeJS.Timeout;
    onMounted(() => {
      refetchInterval = setInterval(() => {
        if (loaded.value && groupOrder.value?.status == "active") {
          refetchGroupOrder();
        }
      }, 10000);
    });
    onUnmounted(() => {
      if (refetchInterval) {
        clearInterval(refetchInterval);
      }
    });
    onUnmounted(() => {
      // Don't clear groupOrder.value - let it persist for cart loading on route transitions
      // Only disable realtime connection to save resources
      realtime.disable();
      realtime.channelId.value = nanoid();
      // Don't clear serviceMethod - it's needed for cart persistence
      console.log("ShopGroupOrder unmounting - preserving cart state");
    });
    // Check if all checkouts are completed and have order IDs
    const allCheckoutsCompleted = computed(() => {
      if (!customer.value?.id || !groupOrder.value?.checkouts) {
        return false;
      }

      // Get current user's checkouts from groupOrder
      const userCheckouts = groupOrder.value.checkouts.filter(
        (checkout) => checkout.customer_id === customer.value?.id,
      );

      if (userCheckouts.length === 0) return false;

      // Check if all user's checkouts have order_id
      return userCheckouts.every((checkout) => checkout.order_id);
    });

    const completedOrderIds = computed(() => {
      if (
        !allCheckoutsCompleted.value ||
        !customer.value?.id ||
        !groupOrder.value?.checkouts
      ) {
        return [];
      }

      // Get current user's checkouts from groupOrder and extract order_ids
      return groupOrder.value.checkouts
        .filter((checkout) => checkout.customer_id === customer.value?.id)
        .map((checkout) => checkout.order_id)
        .filter(Boolean) as number[];
    });
    return () => {
      return (
        <>
          <ShopLayout>
            {loaded.value ? (
              <RouterView />
            ) : (
              <div class="flex-1 flex justify-center items-center">
                <Loading />
              </div>
            )}
          </ShopLayout>

          {/* Join Group Modal */}
          {showJoinModal.value && (
            <JoinGroupModal
              onClose={() => (showJoinModal.value = false)}
              onJoin={handleJoin}
              isLoading={isJoining.value}
              characterPackName={groupOrder.value?.character_pack_name}
            />
          )}
        </>
      );
    };
  },
});
